import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { useCollapsibleToggle } from '@hooks/useCollapsibleToggle'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useMediaSize } from '@hooks/useMediaSize'
import { useModules } from '@hooks/useModules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View, XStack, YStack, isWeb } from 'tamagui'
import { CourseContentConsumptionGhostLoading } from './CourseContentConsumptionGhostLoading'
import { LessonLayout } from './LessonLayout'
import { MobileLayout } from './MobileLayout'
import { useImageStatus } from '@hooks/useImageStatus'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useAvailableLessons } from '@hooks/useAvailableLessons'
import { useGetCourseContent } from '@services/api/graphql/getCourseContent'
import { useLocalSearchParams } from 'expo-router'

export function CourseContentConsumption() {
  const { courseId, enrollmentId, courseContentsId } = useLocalSearchParams()
  const { data: courseContent, loading: isLoadingCourseContent } =
    useGetCourseContent({
      id: Number(courseContentsId),
      courseId: Number(courseId),
      enrollmentId,
    })
  const initialLesson = courseContent?.courseContent?.lesson ?? null
  const [currentLessonId, setCurrentLessonId] = useState<string | number>(0)
  const onLessonIdChange = useCallback((lessonId: string | number) => {
    setCurrentLessonId(lessonId)
  }, [])
  const {
    hasNextContent,
    hasPreviousContent,
    setHasNextContent,
    setHasPreviousContent,
    resetNextAndPreviousContent,
    getIfHasNextAvailableLesson,
    getIfHasPreviousAvailableLesson,
  } = useAvailableLessons(currentLessonId)
  const mediaSize = useMediaSize()
  const isMobile = ['xs', 'sm'].includes(mediaSize)
  const { isExpanded, isHighlightedImage, reset } = useImageStatus()
  const { course } = useCourse()
  const { enrollment } = useEnrollment()
  const { modules } = useModules()
  const { t } = useTranslation('CourseDetails')
  const { isCollapsibleOpen, toggleCollapsible } = useCollapsibleToggle()
  const { isPreview } = useAdminPreview()

  // No preview não há matrícula, e a navegação não depende dela: o servidor
  // devolve a próxima aula ignorando progresso e datas, igual ao legado.
  const refreshHasNextContent = useCallback(async () => {
    if (!currentLessonId) return
    if (!enrollment?.id && !isPreview) return
    const data = await getIfHasNextAvailableLesson(
      Number(currentLessonId),
      enrollment?.id,
    )
    setHasNextContent(Boolean(data?.nextAvailableLesson?.id))
  }, [
    currentLessonId,
    enrollment?.id,
    getIfHasNextAvailableLesson,
    setHasNextContent,
    isPreview,
  ])

  useEffect(() => {
    refreshHasNextContent()
  }, [refreshHasNextContent])

  // `hasPreviousContent` não entra nas deps: este effect é quem seta esse
  // estado, então incluí-lo fazia o effect rodar de novo depois do próprio
  // setState e disparar HasPreviousAvailableLesson duas vezes por aula.
  const refreshHasPreviousContent = useCallback(async () => {
    if (!currentLessonId) return
    if (!enrollment?.id && !isPreview) return
    const data = await getIfHasPreviousAvailableLesson(
      Number(currentLessonId),
      enrollment?.id,
    )
    setHasPreviousContent(Boolean(data?.previousAvailableLesson?.id))
  }, [
    currentLessonId,
    enrollment?.id,
    getIfHasPreviousAvailableLesson,
    setHasPreviousContent,
    isPreview,
  ])

  useEffect(() => {
    refreshHasPreviousContent()
  }, [refreshHasPreviousContent])

  const courseHasContent = useMemo(() => {
    if (!modules?.nodes) return false

    return modules.nodes.some((module) => {
      if (module.courseContents?.nodes.length > 0) return true
      if (module.courseModules.length > 0) {
        return module.courseModules.some(
          (submodule) => submodule.courseContents?.nodes.length > 0,
        )
      }
      return false
    })
  }, [modules])

  useEffect(() => {
    reset()
  }, [reset])

  if (
    !course ||
    !enrollment ||
    !modules ||
    (isLoadingCourseContent && !initialLesson)
  ) {
    return <CourseContentConsumptionGhostLoading />
  }

  if (!courseHasContent) {
    return (
      <YStack
        w="100%"
        backgroundColor="$bg-page"
        testID="course-content-consumption"
      >
        <XStack h={60} w="100%" backgroundColor="$bg-chrome">
          <View flex={1} marginBottom={50}>
            <EmptyStateComponent
              title={t('emptyState.title')}
              description={t('emptyState.description')}
            />
          </View>
        </XStack>
      </YStack>
    )
  }

  return (
    <YStack
      w="100%"
      backgroundColor="$bg-page"
      testID="course-content-consumption"
      mt={isMobile && isHighlightedImage ? -70 : 0}
      animation="bouncy"
      transition="all 0.3s ease-in-out"
    >
      <XStack
        h={isMobile && isExpanded ? '$spacing-xs' : 60}
        w="100%"
        backgroundColor="$bg-chrome"
      />

      <YStack flex={1} w="100%">
        {/* No web a aula é uma árvore só em qualquer largura: trocar de
            componente ao cruzar o breakpoint desmontava o player, e o vídeo
            voltava ao início quando o aluno redimensionava a janela. O app
            continua escolhendo pelo tamanho da tela. */}
        {isWeb || !isMobile ? (
          <LessonLayout
            hasPreviousContent={hasPreviousContent ?? false}
            hasNextContent={hasNextContent ?? false}
            isCollapsibleOpen={isCollapsibleOpen}
            toggleCollapsible={toggleCollapsible}
            resetNextAndPreviousContent={resetNextAndPreviousContent}
            onLessonIdChange={onLessonIdChange}
            onLessonCompleted={refreshHasNextContent}
            initialLesson={initialLesson}
            isLoadingCourseContent={isLoadingCourseContent}
          />
        ) : (
          <MobileLayout
            hasPreviousContent={hasPreviousContent ?? false}
            hasNextContent={hasNextContent ?? false}
            resetNextAndPreviousContent={resetNextAndPreviousContent}
            onLessonIdChange={onLessonIdChange}
            onLessonCompleted={refreshHasNextContent}
            initialLesson={initialLesson}
            isLoadingCourseContent={isLoadingCourseContent}
          />
        )}
      </YStack>
    </YStack>
  )
}
